import { PropsWithChildren, Suspense, useEffect } from 'react'
import { Await, useLoaderData } from 'react-router-dom'

import FullPageLoader from './FullPageLoader'
import FullPageError from './FullPageError'

import { withAngularJSCompatibility } from '$utils'

/**
 * Component that will ensure any wrapped children are rendered only after the
 * data required for the page has been loaded (based on React Router config),
 * a loading page is displayed in the interim.
 * @param props
 * @returns
 */
const PageLoadingWrapper = (props: PropsWithChildren) => {
  // This is the data that is returned by the React Router `loader` property.
  // It's something that needs to be have been wrapped in the react-router
  // `defer` function so that it is compatible with the `Await` component
  // we wrap the child component in. The `Await` component is responsible for
  // rendering the fallback loader up until the data is available - i.e.
  // when the promises deferred are all resolved.
  //
  // Note we're ignoring the type here as it's not possible to know what the
  // defered loader data is going to be now - see https://github.com/remix-run/react-router/discussions/9792
  // for discussion about how to type this safely.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data }: any = useLoaderData()

  return (
    <Suspense fallback={<FullPageLoader />}>
      <Await resolve={data} errorElement={<FullPageError />}>
        {props.children}
      </Await>
    </Suspense>
  )
}

const PageLoadingWithAngularJSCompatibility =
  withAngularJSCompatibility(PageLoadingWrapper)

type PageLoadingSupportProps = PropsWithChildren & {
  /**
   * Should this component be rendered as a AngularJS aware portal? This
   * should only be used once for the root component of our app, in most
   * cases you shouldn't use this. If you do use it you'll introduce the
   * risk of multiple React DOM trees rendered at the same time if/when
   * a route renders a component that is wrapped with this. That becomes
   * a problem when the content is supposed to be rendered within the
   * expected tree (e.g. the file viewer parent component will no longer
   * be a parent of the page content which is never expected to happen).
   */
  enableAngularJSCompatibility?: boolean

  /**
   * This is for setting the web page title that display in the browser tab
   */
  title?: string
}

/**
 * A wrapper component that is expected to encapsulate the page content on a
 * React Router route. It'll show a loading page until the data required is
 * available.
 * @param Component A React component representing the page content.
 * @returns
 */
const PageLoadingSupport = ({
  enableAngularJSCompatibility = false,
  children,
  title,
}: PageLoadingSupportProps) => {
  useEffect(() => {
    document.title = title ? `Loaded - ${title}` : 'Loaded'
  }, [title])

  const WrapperComponent = enableAngularJSCompatibility
    ? PageLoadingWithAngularJSCompatibility
    : PageLoadingWrapper

  return <WrapperComponent>{children}</WrapperComponent>
}

export default PageLoadingSupport
